import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClose,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';

import { ShadowPopupWrapper } from '@client/components/common/Modal';
import { Button } from '@client/components/common/inputs/Button';
import summaryPreview from '@client/images/previews/summary-preview.png';
import faqs from '@client/images/previews/faqs.png';
import testimonials from '@client/images/previews/testimonials.png';
import beforeafter from '@client/images/previews/beforeafter.png';
import { Typography } from '@mui/material';

enum TPreviewType {
  mobile = 'mobile',
  desktop = 'desktop',
}

const CloseModalIcon = ({ onClose, position = 'right' }) => {
  const posStyles = position === 'right' ? 'right-6 top-6' : 'left-6 top-6';

  return (
    <FontAwesomeIcon
      icon={faClose as any}
      className={`leading-none text-sm w-[24px] h-[24px] absolute ${posStyles} cursor-pointer`}
      onClick={onClose}
    />
  );
};

const PreviewModal = ({ data, onClose, previewType, previewUrl }) => {
  const formRef = useRef<HTMLFormElement>(null!);
  useEffect(() => {
    formRef.current.submit();
  }, [data]);

  const previewWidth =
    previewType === TPreviewType.desktop
      ? 'w-[90vw]'
      : previewType === TPreviewType.mobile
      ? 'w-[375px]'
      : 'w-[90vw]';

  const closeIconPosition =
    previewType === TPreviewType.desktop ? 'right' : 'left';

  return (
    <>
      <form
        ref={formRef}
        action={previewUrl}
        method="post"
        target="preview_frame"
        className="hidden"
      >
        <input name="pageConfig" value={JSON.stringify(data)} readOnly />
        <input
          type="checkbox"
          name="isMobile"
          checked={previewType === 'mobile'}
          hidden
        />
      </form>
      <ShadowPopupWrapper {...{ onClose }}>
        <div
          className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 ${previewWidth} h-[90vh] bg-white shadow-lg`}
        >
          <CloseModalIcon onClose={onClose} position={closeIconPosition} />
          <iframe className={`w-full h-full`} name="preview_frame" />
        </div>
      </ShadowPopupWrapper>
    </>
  );
};

const SummaryModal = ({ onClose }) => {
  const summaryHeader = 'Summary';

  const summaryDescription = `
    Here you can edit the top portion of your sales page — headline, intro video, and cover image.
    Plus, you can set the color of your website.`;

  return (
    <ShadowPopupWrapper {...{ onClose }}>
      <div className="lbp-modal absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white shadow-lg px-[48px] py-[56px] w-full md:w-[60vw]">
        <CloseModalIcon onClose={onClose} />
        <Typography variant="h1" className="text-center font-bold">
          {summaryHeader}
        </Typography>
        <p className="text-center text-base opacity-75 max-w-[600px] m-auto mb-12">
          {summaryDescription}
        </p>
        <div className="flex justify-around flex-wrap">
          <img
            src={summaryPreview}
            alt=""
            className="flex-shrink flex h-[100%] w-[100%] max-w-[500px] max-h-[100%] mb-8 mr-0 lg:mb-0 lg:mr-0"
          />
          <div className="max-w-[350px] min-w-[350px]">
            <div>
              <h4 className="text-lg mb-0">Headline text</h4>
              <p className="text-base">
                Use your headline to communicate how your app is valuable and
                what subscriber's will gain by signing up.
              </p>
            </div>
            <div>
              <h4 className="text-lg mb-0">Intro Video & Image</h4>
              <p className="text-base">
                The cover image is used as the placeholder/preview for the intro
                video.
              </p>
            </div>
            <div>
              <h4 className="text-lg mb-0">Color</h4>
              <p className="text-base mb-0">
                The color you choose will apply to all elements on this landing
                page.
              </p>
            </div>
          </div>
        </div>
      </div>
    </ShadowPopupWrapper>
  );
};

const ReviewsModal = ({ onClose }) => {
  const reviewHeader = 'Reviews';
  const reviewDescription = 'Add new reviews here by copy/pasting.';

  const Review = ({ name, text }) => {
    return (
      <div className="p-5 bg-[#F9FAFB] max-w-[300px] m-4">
        <h5>
          {name} <span className="ml-2 text-yellow-400">★★★★★</span>
        </h5>
        <p>{text}</p>
      </div>
    );
  };

  return (
    <ShadowPopupWrapper {...{ onClose }}>
      <div className="lbp-modal absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full md:w-[66vw] bg-white shadow-lg p-16">
        <CloseModalIcon onClose={onClose} />
        <Typography variant="h1" className="text-center">
          {reviewHeader}
        </Typography>
        <p className="text-center opacity-75 max-w-[500px] m-auto">
          {reviewDescription}
        </p>
        <div className="flex mt-12 border-solid border-2 border-black rounded-md p-2 flex-wrap justify-center items-center">
          <Review
            name="Carrie"
            text="Love this app!!! Every workout is challenging ande I've had amazing results"
          />
          <Review
            name="Laura"
            text="Beautiful, user friendly app. Truly enjoying having quick access to workouts. Makes reaching my goal of daily movement much easier"
          />
          <Review
            name="Jess"
            text="Clear, straightforward, focused workouts that are enjoyable and effective"
          />
        </div>
      </div>
    </ShadowPopupWrapper>
  );
};

const FaqsModal = ({ onClose }) => {
  const faqHeader = 'FAQs';
  const faqDescription = 'Edit FAQs, add new ones, and even reorder.';

  return (
    <ShadowPopupWrapper {...{ onClose }}>
      <div className="lbp-modal absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[100vw] md:w-[700px] bg-white shadow-lg p-10">
        <CloseModalIcon onClose={onClose} />
        <Typography variant="h1" className="text-center">
          {faqHeader}
        </Typography>
        <p className="text-center opacity-75 max-w-[500px] m-auto">
          {faqDescription}
        </p>
        <img
          src={faqs}
          alt=""
          className="mt-12 mb-4 mx-auto h-[100%] w-[100%] max-w-[600px] max-h-[100%]"
        />
      </div>
    </ShadowPopupWrapper>
  );
};

const TestimonialsModal = ({ onClose }) => {
  const header = 'Testimonials';
  const description =
    "In this section, you can showcase examples of your clients' before/after photos and body transformations alongside personal testimonials.";

  return (
    <ShadowPopupWrapper {...{ onClose }}>
      <div className="lbp-modal absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[100vw] md:w-[700px] bg-white shadow-lg p-10">
        <CloseModalIcon onClose={onClose} />
        <Typography variant="h1" className="text-center">
          {header}
        </Typography>
        <p className="text-center opacity-75 max-w-[500px] m-auto">
          {description}
        </p>
        <img
          src={testimonials}
          alt=""
          className="mt-12 mb-4 mx-auto h-[100%] w-[100%] max-w-[600px] max-h-[100%]"
        />
      </div>
    </ShadowPopupWrapper>
  );
};

export const BeforeAfterModal = ({ onClose }) => {
  const header = 'Before/After Image';
  const description =
    'For best results, we recommend the following to create visually beautiful before/after photos:';

  return (
    <ShadowPopupWrapper {...{ onClose }}>
      <div className="lbp-modal absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[100vw] md:w-[867px] bg-white shadow-lg p-10">
        <CloseModalIcon onClose={onClose} />
        <div className="flex items-center">
          <img
            src={beforeafter}
            alt=""
            className="flex-basis[270px] max-w-[270px] max-h-[270px]"
          />
          <div className="ml-[50px] flex-shrink flex-basis-[363px]">
            <Typography variant="h1" className="text-gray-1">
              {header}
            </Typography>
            <p className="text-gray-2">{description}</p>
            <br />
            <ul className="text-gray-1 list-disc ml-[30px]">
              <li>Put 2 vertical photos side-by-side</li>
              <li>Avoid any dividing lines between the 2 photos</li>
              <li>Final image should be square</li>
              <li>
                See our{' '}
                <a
                  href="https://www.canva.com/design/DAFvBafZGLE/Jzl_s8ChkDTnk1KecOK7xA/view?utm_content=DAFvBafZGLE&utm_campaign=designshare&utm_medium=link&utm_source=publishsharelink&mode=preview"
                  target="_blank"
                >
                  Canva
                </a>{' '}
                template
              </li>
            </ul>
          </div>
        </div>
      </div>
    </ShadowPopupWrapper>
  );
};

const SubmitErrorModal = ({ onClose }) => {
  return (
    <ShadowPopupWrapper {...{ onClose }}>
      <div
        className={`flex flex-col justify-center items-center absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[358px] bg-white rounded-[10px]`}
      >
        <CloseModalIcon onClose={onClose} />
        <FontAwesomeIcon
          icon={faTriangleExclamation as any}
          className="leading-none text-gray-400 mb-4 w-[54px] h-[48px]"
        />
        Something went wrong. Please try again or contact us.
        <Button className="mt-3" onClick={onClose}>
          Okay
        </Button>
      </div>
    </ShadowPopupWrapper>
  );
};

export const ErrorVideoModal = ({ errorType, onClose }) => {
  const ErrorText = () => {
    if (errorType === 'duration') {
      return (
        <>
          <Typography variant="h1">Your video is too long</Typography>
          <p>Max 90 seconds only</p>
        </>
      );
    }

    if (errorType === 'format') {
      return (
        <>
          <Typography variant="h1">Wrong format</Typography>
          <p>MP4 only</p>
        </>
      );
    }

    if (errorType === 'size') {
      return (
        <>
          <Typography variant="h1">Your video is too big</Typography>
          <p>Max 100 mb</p>
        </>
      );
    }
  };
  return (
    <ShadowPopupWrapper {...{ onClose }}>
      <div
        className={`flex flex-col justify-center items-center absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[358px] bg-white rounded-[10px]`}
      >
        <CloseModalIcon onClose={onClose} />
        <FontAwesomeIcon
          icon={faTriangleExclamation as any}
          className="leading-none text-gray-400 mb-4 w-[54px] h-[48px]"
        />
        <ErrorText />
        <Button className="mt-3" onClick={onClose}>
          Reload
        </Button>
      </div>
    </ShadowPopupWrapper>
  );
};

const HideWarningModal = ({ onClose }) => {
  return (
    <ShadowPopupWrapper {...{ onClose }}>
      <div
        className={`flex flex-col justify-center items-center absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[358px] bg-white rounded-[10px] font-[Gilroy]`}
      >
        <CloseModalIcon onClose={onClose} />
        <Typography
          variant="h1"
          className="text-h32 font-bold text-gray-1 mb-0"
        >
          Hide this section?
        </Typography>
        <h2 className="text-h16 font-medium text-gray-2 max-w-[287px] text-center mb-0 mt-[20px]">
          If you hide this section, it will not be displayed on your landing
          page
        </h2>
        <div className="flex space-x-[20px] mt-[40px]">
          <Button onClick={() => onClose(false)} variant="gray">
            Cancel
          </Button>
          <Button onClick={() => onClose(true)}>Okay</Button>
        </div>
      </div>
    </ShadowPopupWrapper>
  );
};
